import { Link } from "react-router-dom";
import type { Recap } from "../lib/runRecap";
import { reviewQueueHref } from "../lib/reviewTarget";
import { HISTORY_LINK } from "../lib/nextStep";

// RunRecap (0027 P4; 0051 slice 2): the next-step bar's result lines - what
// the last pass changed, in founder terms, read from the run record through
// the product's next step, so it survives a closed tab. In order: the headline
// (none on a no-op recap whose earlier answers came back); the earlier answers
// the pass handed back for a covered-by check (0164 R4), a link to where
// Product says the first one waits; the decisions it withdrew because a new
// answer replaced them (0157-15 Q11) and the saved choices it could not apply
// (0159 F3), each a link to the Changes page; the layers it settled, a button
// that opens the spec column at the lowest of them while the page holds it,
// plain words otherwise; and History. The act is the bar's; no dismiss: the
// result stays until the server's exit.
export function RunRecap({
  recap,
  productId,
  checkHref,
  onReadSettled,
  settledOpens,
}: {
  recap: Recap;
  productId: string;
  // 0164 R4: where the earlier-answer line lands - Product passes the first
  // undrafted question awaiting the covered-by ruling; the questions queue at
  // pos 0 when absent.
  checkHref?: string;
  // The settled line's act: Product opens the spec column at that layer.
  onReadSettled: () => void;
  // Whether the page holds a layer the settled line opens.
  settledOpens: boolean;
}) {
  return (
    <div className="space-y-1">
      {recap.headline !== "" && <p className="text-sm text-ink-100">{recap.headline}</p>}
      {/* 0164, founder ruling R4: the earlier answers this run handed
          back for a covered-by check, on EVERY recap (a no-op one
          included), linked to where the first one waits. Directly under
          the headline: it is founder work this run created, like the
          headline's; the Changes-page lines follow. */}
      {recap.returnedAnswerLine && (
        <Link
          to={checkHref ?? reviewQueueHref(productId, "questions", 0)}
          data-testid="recap-returned-answer"
          className="block text-[13px] text-ink-300 underline-offset-2 hover:text-ink-100 hover:underline"
        >
          {recap.returnedAnswerLine}
        </Link>
      )}
      {/* 0157-15 Q11: the decisions this run withdrew because a new answer
          replaced them, on EVERY recap (a no-op one included), linked to
          the Changes entry whose summary names each card. Before the
          saved-choices line because the run summary carries the two in
          that order (the withdrawn line, then the set-aside line). */}
      {recap.withdrawnLine && (
        <Link
          to={`/products/${productId}/changes`}
          data-testid="recap-withdrawn"
          className="block text-[13px] text-ink-300 underline-offset-2 hover:text-ink-100 hover:underline"
        >
          {recap.withdrawnLine}
        </Link>
      )}
      {/* 0159 F3: the saved choices this run could not apply, on EVERY
          recap (a no-op one included), linked to the Changes entry that
          names them. */}
      {recap.setAsideLine && (
        <Link
          to={`/products/${productId}/changes`}
          data-testid="recap-set-aside"
          className="block text-[13px] text-ink-300 underline-offset-2 hover:text-ink-100 hover:underline"
        >
          {recap.setAsideLine}
        </Link>
      )}
      {/* 0051 slices 2-3: every layer the pass closed. A button only while
          the page holds one of them, never one that scrolls to nothing. */}
      {recap.settledLine &&
        (settledOpens ? (
          <button
            type="button"
            onClick={onReadSettled}
            data-testid="recap-settled"
            className="block text-left text-[13px] text-ink-300 underline-offset-2 hover:text-ink-100 hover:underline"
          >
            {recap.settledLine}
          </button>
        ) : (
          <p data-testid="recap-settled" className="text-[13px] text-ink-300">
            {recap.settledPlain}
          </p>
        ))}
      {!recap.isNoOp && (
        <Link
          to={`/products/${productId}/changes`}
          data-testid="recap-history"
          className="inline-block pt-1 font-mono text-[11px] text-ink-400 hover:text-ink-200 transition-colors"
        >
          {HISTORY_LINK}
        </Link>
      )}
    </div>
  );
}
